import DetailTemplate from '@/components/detail-template'
import React, { useEffect, useMemo, useState } from 'react'
import { displayModel, PluginDetailProps } from './display-model'
import { FilterDropdownProps } from 'antd/lib/table/interface'
import {
  useAddReport,
  useDeleteList,
  useGetAttackType,
  useGetAttck,
  useGetOfflineLogImportInfo,
  useGetProtectRate,
  useGetResultInfo,
  useGetRiskRate,
  useGetTagTreeOptions,
  useGetUseCaseList,
  useGetResultPluginDetail,
  useDeleteOfflineLogImportInfo,
  useUpdateResourceInterceptStatus,
  useGetCancelVerifyCaseList,
  useGetSentFailedCaseList,
  useGetUnsentCaseList,
  useGetVerifyResultDistribution,
  useGetCaseLevelDistribution,
  useGetTemplateProtectionFailureRate,
  useGetDeviceProtectionRanking,
  useGetTemplateNameList,
  useGetTaskDetailFilterData,
} from './utils'
import styled from 'styled-components'
import { Button, Divider, Form, Progress, Select, Space, Tooltip, Typography } from 'antd'
import {
  DeleteModal,
  IconFont,
  ImportModal,
  RecordModal,
  SvgIcon,
  TagSearchOption,
} from '@/components'
import leakSeverity from '@/assets/imges/leak-severity.png'
import leakCenter from '@/assets/imges/leak-center.png'
import leakLow from '@/assets/imges/leak-low.png'
import leakHigh from '@/assets/imges/leak-high.png'
import TaskCharts from './task-charts'
import { DetailDrawer } from './detail-drawer'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CreateReport } from '@/components/create-report'
import { formatDateTime } from '@/utils/date'
import ProgressContent from '@/components/detail-template/widgets/progress-content'
import { FilterValue, SorterResult, SortOrder } from 'antd/lib/table/interface'
import { TablePaginationConfig } from 'antd/es/table'
import { t } from '@/i18n'

export type CorrectedDataProps = {
  interceptStatus?: string
  resourceId?: string
  editable?: boolean
  prevInterceptStatus?: string
  isCorrected?: boolean
}

const { Text } = Typography
const { useForm } = Form

const interceptStatusMap = {
  InterceptStatusSuccess: { label: t('拦截成功'), class: 'intercept-success' },
  InterceptStatusDefenceFail: { label: t('防护失效'), class: 'intercept-fail' },
  InterceptStatusCancel: { label: t('取消验证'), class: 'intercept-cancel' },
  InterceptStatusDetectSuccess: { label: t('检测成功'), class: 'test-success' },
}

const logAlarmMap = {
  1: { label: t('有告警'), class: 'log-alarm-success' },
  2: { label: t('无告警'), class: 'log-alarm-fail' },
}

const deviceStatusMap = {
  ResourceDeviceStatusInterceptSuccess: { label: t('拦截成功'), class: 'device-success' },
  ResourceDeviceStatusProtectionInvalid: { label: t('防护失效'), class: 'device-fail' },
  ResourceDeviceStatusNotInvolved: { label: t('取消验证'), class: 'device-cancel' },
  ResourceDeviceStatusDetectSuccess: { label: t('检测成功'), class: 'device-test' },
}

const statusMap = {
  2: { label: t('进行中'), color: '#2973FF', background: '#DBE8FF' },
  3: { label: t('已完成'), color: '#67c23a', background: '#E4F7D1' },
  4: { label: t('中断'), color: '#F53F3F', background: '#FFDCE0' },
  7: { label: t('停止中'), color: '#FF9900', background: '#FFEFD7' },
}

// 添加格式化时间间隔的函数
const formatTimeInterval = (startTime: string, endTime: string) => {
  if (!startTime || !endTime) {
    return ' -'
  }
  const interval = new Date(endTime).getTime() - new Date(startTime).getTime()
  const totalSeconds = Math.floor(interval / 1000)

  if (totalSeconds < 60) {
    return `${totalSeconds}${t('秒')}`
  }

  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) {
    return `${hours}${t('小时')}${minutes}${t('分')}${seconds}${t('秒')}`
  } else if (minutes > 0) {
    return `${minutes}${t('分')}${seconds}${t('秒')}`
  }

  return `${seconds}${t('秒')}`
}

const Detail: React.FC = () => {
  const navigate = useNavigate()
  const [form] = useForm()
  const [searchParams] = useSearchParams()
  const id = searchParams.get('id') as string
  const task_id = searchParams.get('task_id') as string
  // 获取任务信息
  const [getTaskInfo, taskInfo] = useGetResultInfo()
  // 获取安全设备
  // const [getDevices, devices] = useGetDevices()
  // 获取攻击类型
  const [getAttackType, attackType] = useGetAttackType()
  // 获取 ATT&CK
  const [getAttck, attckList] = useGetAttck()
  // 获取用例列表
  const [getUseCaseList, loading] = useGetUseCaseList()
  // 修改拦截状态
  const [updateResourceInterceptStatus, updateInterceptStatusLoading] =
    useUpdateResourceInterceptStatus()
  // 获取标签组列表
  const [getTagTreeOptions, tagTreeOptions] = useGetTagTreeOptions()
  // 获取验证结果分布 （图表）
  const [getVerifyResultDistribution, verifyResultDistribution] = useGetVerifyResultDistribution()
  // 获取攻击防护排名 （轮播）
  const [getProtectRate, protectedRate] = useGetProtectRate()
  // 获取用例等级分布 （图表）
  const [getCaseLevelDistribution, caseLevelDistribution] = useGetCaseLevelDistribution()
  // 获取危险等级分布 （图表）
  const [getRiskRate, riskRate] = useGetRiskRate()
  // 获取模板防护失效率排名 （图表）
  const [getTemplateProtectionFailureRate, templateProtectionFailureRate] =
    useGetTemplateProtectionFailureRate()
  // 获取设备防护能力排名 （图表）
  const [getDeviceProtectionRanking, deviceProtectionRanking] = useGetDeviceProtectionRanking()

  // 获取离线日志导入信息
  const [getOfflineLogInfo, importRecord] = useGetOfflineLogImportInfo()
  // 删除日志设备导入记录
  const [deleteOfflineLogInfo, deleteRecordLoading] = useDeleteOfflineLogImportInfo()
  // 生成报表
  const [generateReport, createLoading] = useAddReport()
  // 删除任务
  const [deleteTask] = useDeleteList()
  // 抽屉详情
  const [getResultPluginDetail, data, detailLoading] = useGetResultPluginDetail()
  // 获取执行失败用例列表
  const [getSentFailedCaseList, sentFailedCaseList] = useGetSentFailedCaseList()
  // 获取未执行用例列表
  const [getUnsentCaseList, unsentCaseList] = useGetUnsentCaseList()
  // 获取取消验证用例列表
  const [getCancelVerifyCaseList, cancelVerifyCaseList] = useGetCancelVerifyCaseList()
  // 获取模板名称列表
  const [getTemplateNameList, templateNameList] = useGetTemplateNameList()
  // 获取任务详情用例列表筛选数据
  const [getTaskDetailFilterData, taskDetailFilterData] = useGetTaskDetailFilterData()
  const { taskInfo: taskInfoModel } = displayModel

  // 控制 执行日志 显隐
  const [logInfo, setLogInfo] = useState<{ visible: boolean; content: string; id?: string }>({
    visible: false,
    content: '',
  })
  // 筛选条件
  const [filterParams, setFilterParams] = useState<{
    page: number
    limit: number
    [key: string]: any
  }>({
    page: 1,
    limit: 20,
  })
  // 用例列表
  const [useCaseList, setUseCaseList] = useState<any[]>([])
  // 用例总数
  const [useCaseTotal, setUseCaseTotal] = useState(0)
  // 修正用例数量
  const [fixPayloadCount, setFixPayloadCount] = useState(0)
  // 选中项的 key 数组
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  // 导入记录弹窗 visible
  const [recordVisible, setRecordVisible] = useState(false)
  // 导入离线日志弹窗 visible
  const [importVisible, setImportVisible] = useState(false)
  // 生成报表弹窗 visible
  const [reportVisible, setReportVisible] = useState({
    visible: false,
    result_id: id,
    name: taskInfo.task_name,
  })
  // 删除弹窗 visible
  const [deleteVisible, setDeleteVisible] = useState(false)
  // 控制 详情抽屉 显隐
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false)
  // 详情抽屉 数据
  const [pluginDetail, setPluginDetail] = useState<PluginDetailProps>()
  // 批量修正弹窗
  const [fixedModalVisible, setFixedModalVisible] = useState(false)
  const [activeTab, setActiveTab] = useState('1')
  const [activeRightTab, setActiveRightTab] = useState('1')
  const [activeProgressTab, setActiveProgressTab] = useState('')
  const [filterFormValue, setFilterFormValue] = useState<{
    field: string | number
    value: string | number | string[] | number[] | undefined
  }>({
    field: '',
    value: '',
  })
  const [attackTypeSelectOpen, setAttackTypeSelectOpen] = useState(false)
  const [deviceStatusSelectOpen, setDeviceStatusSelectOpen] = useState(false)
  const [riskLevelSelectOpen, setRiskLevelSelectOpen] = useState(false)
  const [resourceLevelSelectOpen, setResourceLevelSelectOpen] = useState(false)
  const [interceptStatusSelectOpen, setInterceptStatusSelectOpen] = useState(false)
  const [statusSelectOpen, setStatusSelectOpen] = useState(false)
  const [failSum, setFailSum] = useState(0)
  const [unexecuteSum, setUnexecuteSum] = useState(0)
  const [unvalidSum, setUnvalidSum] = useState(0)

  let option = [
    {
      label: t('用例名称'),
      value: 'keyword',
      type: 'input' as const,
    },
    {
      label: 'ATT&CK',
      value: 'attack',
      type: 'cascader',
      mode: 'multiple',
      options: attckList?.map(item => ({
        label: item.name,
        value: item.id,
        children: item?.technology?.map(tech => ({
          label: tech.name,
          value: tech.id,
          children: tech?.subTechnology?.map(subTech => ({
            label: subTech.name,
            value: subTech.id,
          })),
        })),
      })),
    },
    {
      label: t('模板名称'),
      value: 'templateIdList',
      type: 'select',
      mode: 'multiple',
      options: [
        ...templateNameList.map(item => ({
          label: item.templateName || '-',
          value: item.templateId,
        })),
      ],
    },
    {
      label: t('用例标签'),
      value: 'tagIds',
      type: 'treeSelect',
      mode: 'multiple',
      options: TagSearchOption(tagTreeOptions),
    },
  ]

  let columns = [
    {
      title: t('用例名称'),
      dataIndex: 'pluginName',
      key: 'pluginName',
      width: 140,
      fixed: 'left',
      render: (_, record: any) => (
        <Text
          ellipsis={{
            tooltip: {
              getPopupContainer: () => document.body,
            },
          }}
          className="case_name"
          style={{ width: 300, cursor: 'pointer' }}
          onClick={() => {
            getDrawerData(record.id)
          }}
        >
          {record?.pluginName}
        </Text>
      ),
    },
    {
      title: t('危险等级'),
      dataIndex: 'riskLevel',
      key: 'riskLevel',
      width: 100,
      filteredValue: filterParams.riskLevel,
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            allowClear
            maxTagCount={'responsive'}
            style={{ width: '100%' }}
            placeholder={t('请选择危险等级')}
            value={props.selectedKeys as string[]}
            open={riskLevelSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            popupClassName="table-filter-dropdown"
            onDropdownVisibleChange={setRiskLevelSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setRiskLevelSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={[
              { label: t('严重'), value: 'risk_level_critical' },
              { label: t('高危'), value: 'risk_level_high' },
              { label: t('中危'), value: 'risk_level_medium' },
              { label: t('低危'), value: 'risk_level_low' },
            ]}
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),

      render: (level: string) => {
        const levelMap = {
          risk_level_critical: t('严重'),
          risk_level_high: t('高危'),
          risk_level_medium: t('中危'),
          risk_level_low: t('低危'),
        }
        const config = {
          严重: { color: '#B70000', bg: '#F5D9D9' },
          高危: { color: '#FF4141', bg: '#FFE1E1' },
          中危: { color: '#FFA709', bg: '#FFEDCE' },
          低危: { color: '#6F9FFF', bg: '#E1EBFE' },
        }
        const displayLevel = levelMap[level] || t('低危')
        const style = config[displayLevel]
        return (
          <span>
            <img
              src={
                level === 'risk_level_critical'
                  ? leakSeverity
                  : level === 'risk_level_high'
                  ? leakHigh
                  : level === 'risk_level_medium'
                  ? leakCenter
                  : leakLow
              }
              alt=""
              style={{ width: '12px', height: '12px', marginRight: '4px' }}
            />

            <TypeTag bgColor={style?.bg} color={style?.color}>
              {displayLevel}
            </TypeTag>
          </span>
        )
      },
    },
    {
      title: t('攻击类型'),
      dataIndex: 'attackType',
      key: 'attackType',
      width: 120,
      filteredValue: filterParams.attackType,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            value={selectedKeys as string[]}
            open={attackTypeSelectOpen}
            onDropdownVisibleChange={setAttackTypeSelectOpen}
            getPopupContainer={() => document.body}
            onChange={value => {
              setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      clearFilters?.()
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      confirm()
                      setAttackTypeSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={attackType.map(item => ({
              value: item,
              label: item,
            }))}
            placeholder={t('请选择')}
            allowClear
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),

      render: (text: string) => (
        <Text
          ellipsis={{ tooltip: { getPopupContainer: () => document.body } }}
          style={{ width: 120 }}
        >
          {text || '-'}
        </Text>
      ),
    },
    {
      title: t('用例分级'),
      dataIndex: 'resourceLevel',
      key: 'resourceLevel',
      width: 100,
      filteredValue: filterParams.resourceLevel,
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            allowClear
            maxTagCount={'responsive'}
            style={{ width: '100%' }}
            placeholder={t('请选择用例分级')}
            value={props.selectedKeys as string[]}
            open={resourceLevelSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            popupClassName="table-filter-dropdown"
            onDropdownVisibleChange={setResourceLevelSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setResourceLevelSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={[
              { label: t('初级'), value: 'ResourceLevelJunior' },
              { label: t('中级'), value: 'ResourceLevelMiddle' },
              { label: t('高级'), value: 'ResourceLevelSenior' },
            ]}
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),

      render: level => {
        const levelMap = {
          ResourceLevelSenior: t('高级'),
          ResourceLevelMiddle: t('中级'),
          ResourceLevelJunior: t('初级'),
          ResourceLevelUnknown: t('未知'),
        }
        const config = {
          高级: { color: '#fff', bg: '#F44F4F' },
          中级: { color: '#fff', bg: '#FFA709' },
          初级: { color: '#fff', bg: '#6F9FFF' },
          未知: { color: '#fff', bg: '#6F9FFF' },
        }
        const displayLevel = levelMap[level] || t('初级')
        const style = config[displayLevel]
        return (
          <TypeTag bgColor={style?.bg} color={style?.color}>
            {displayLevel}
          </TypeTag>
        )
      },
    },
    {
      title: t('验证结果'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      filteredValue: filterParams.status,
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            allowClear
            maxTagCount={'responsive'}
            style={{ width: '100%' }}
            placeholder={t('请选择验证结果')}
            value={props.selectedKeys as string[]}
            open={interceptStatusSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            popupClassName="table-filter-dropdown"
            onDropdownVisibleChange={setInterceptStatusSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setInterceptStatusSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={[
              { label: t('拦截成功'), value: 'InterceptStatusSuccess' },
              { label: t('防护失效'), value: 'InterceptStatusDefenceFail' },
              { label: t('检测成功'), value: 'InterceptStatusDetectSuccess' },
            ]}
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),

      render: (_: any, record: any) => (
        <div>
          <span className={interceptStatusMap[record?.interceptStatus]?.class}>
            {interceptStatusMap[record?.interceptStatus]?.label}
          </span>
        </div>
      ),
    },
    {
      title: t('设备结果分布'),
      dataIndex: 'deviceStatus',
      key: 'deviceStatus',
      width: 150,
      filteredValue: filterParams.deviceStatus,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            value={selectedKeys as string[]}
            open={deviceStatusSelectOpen}
            onDropdownVisibleChange={setDeviceStatusSelectOpen}
            getPopupContainer={() => document.body}
            onChange={value => {
              setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      clearFilters?.()
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      confirm()
                      setDeviceStatusSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={[
              {
                value: 'ResourceDeviceStatusInterceptSuccess',
                label: t('拦截成功'),
              },
              {
                value: 'ResourceDeviceStatusProtectionInvalid',
                label: t('防护失效'),
              },
              {
                value: 'ResourceDeviceStatusDetectSuccess',
                label: t('检测成功'),
              },
            ]}
            placeholder={t('请选择')}
            allowClear
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),

      render: (_: any, record: any) => (
        <div>
          {record?.deviceStatusList?.length > 0 ? (
            record?.deviceStatusList?.length === 1 ? (
              <span className={deviceStatusMap[record?.deviceStatusList[0]?.status]?.class}>
                {deviceStatusMap[record?.deviceStatusList[0]?.status]?.label}
              </span>
            ) : (
              record?.deviceStatusList?.map(item => (
                <Tooltip title={`${deviceStatusMap[item.status]?.label}${item.count}`}>
                  <span key={item.status} className={deviceStatusMap[item.status]?.class}>
                    {item.count}
                  </span>
                </Tooltip>
              ))
            )
          ) : (
            '-'
          )}
        </div>
      ),
    },
    {
      title: t('告警结果'),
      dataIndex: 'alertLog',
      key: 'alertLog',
      width: 100,
      filteredValue: filterParams.alertLog,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            style={{ width: '100%' }}
            value={selectedKeys as string[]}
            getPopupContainer={() => document.body}
            onChange={value => {
              setSelectedKeys(value)
              confirm()
            }}
            options={[
              {
                value: '1',
                label: t('有告警'),
              },
              {
                value: '2',
                label: t('无告警'),
              },
            ]}
            placeholder={t('请选择')}
            allowClear
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),

      render: (text: number) => (
        <div>
          <span className={logAlarmMap[text]?.class}>{logAlarmMap[text]?.label}</span>
        </div>
      ),
    },
    {
      title: t('执行时间'),
      dataIndex: 'execTime',
      key: 'execTime',
      width: 100,
      sorter: true,
      sortDirections: ['ascend', 'descend'] as SortOrder[],
      render: (text: string) => (
        <Tooltip title={formatDateTime(text, 'standard')}>
          {text ? formatDateTime(text, 'date') : '-'}
        </Tooltip>
      ),
    },
    {
      title: t('操作'),
      width: 60,
      fixed: 'right',
      render: (_, record: any) => (
        <div
          className="operation"
          onClick={() => {
            getDrawerData(record.id)
          }}
        >
          <span>{t('详情')}</span>
        </div>
      ),
    },
  ]

  // 如果没有设备数据，则移除设备结果分布列
  if (!(taskInfo?.devices?.length > 0)) {
    columns = columns.filter(col => col.key !== 'deviceStatus')
  }

  const rowSelection = {
    selectedRowKeys,
    onSelect: (record, selected) => {
      let newSelectedRowKeys = [...selectedRowKeys]
      if (selected) {
        newSelectedRowKeys.push(record.id)
      } else {
        newSelectedRowKeys = newSelectedRowKeys.filter(key => key !== record.id)
      }
      setSelectedRowKeys(newSelectedRowKeys)
    },
    onSelectAll: selected => {
      const newSelectedRowKeys = selected ? useCaseList.map(item => item.id) : []
      setSelectedRowKeys(newSelectedRowKeys)
    },
  }

  const fixedOptions = useMemo(() => {
    if (taskInfo.devices?.length > 0) {
      return [
        {
          label: t('拦截成功'),
          value: 'InterceptStatusSuccess',
        },
        {
          label: t('防护失效'),
          value: 'InterceptStatusDefenceFail',
        },
        {
          label: t('检测成功'),
          value: 'InterceptStatusDetectSuccess',
        },
      ]
    } else {
      return [
        {
          label: t('拦截成功'),
          value: 'InterceptStatusSuccess',
        },
        {
          label: t('防护失效'),
          value: 'InterceptStatusDefenceFail',
        },
      ]
    }
  }, [taskInfo.devices])

  const progressContentList = useMemo(() => {
    switch (activeProgressTab) {
      case '1':
        return sentFailedCaseList
      case '2':
        return unsentCaseList
      case '3':
        return cancelVerifyCaseList
      default:
        return []
    }
  }, [activeProgressTab, sentFailedCaseList])

  const getDrawerData = taskPayloadId => {
    setDetailDrawerVisible(true)
    getResultPluginDetail({
      result_id: id || '',
      id: taskPayloadId,
    })
      .then(res => {
        setPluginDetail(res?.data)
      })
      .catch(_ => {})
  }

  // 返回上一页
  const handleBack = () => {
    navigate('/validation/network/endpointsecurity?tab=task')
  }

  // 删除日志设备导入记录
  const deleteRecord = (importRecordId: string) => {
    deleteOfflineLogInfo({ resultId: id, id: importRecordId })
      .then(() => {
        refreshRequest()
        getEchartsData()
      })
      .catch(_ => {})
  }

  // 创建报表
  const createReport = values => {
    generateReport({
      task_list: [{ task_name: reportVisible.name, campaign_id: id }],
      ...values,
    })
      .then(() => {
        window.open('/report/task/task-report?tab=9', '_self')
      })
      .catch(_ => {})
  }

  // 删除任务
  const handleDelete = async () => {
    try {
      if (id) {
        await deleteTask({ result_id_list: [id] })
        navigate('/validation/network/endpointsecurity?tab=task')
      }
    } catch (error) {}
  }

  const handleExpand = async () => {
    setActiveProgressTab('1')
    try {
      const [res1, res2, res3] = await Promise.all([
        getSentFailedCaseList(id),
        getUnsentCaseList(id),
        getCancelVerifyCaseList(id),
      ])
      setFailSum(res1.data.total)
      setUnexecuteSum(res2.data.total)
      setUnvalidSum(res3.data.total)
    } catch (error) {}
  }

  const handleFixed = values => {
    setFixedModalVisible(false)
    const { interceptStatus } = values

    updateResourceInterceptStatus({
      id: id,
      list: {
        taskResultResourceList: selectedRowKeys.map(key => ({
          pluginId: key,
          interceptStatus,
        })),
      },
    })
      .then(_ => {
        form.resetFields()
        setSelectedRowKeys([])
        getTaskInfo({ result_id: id, task_id: task_id })
          .then(res => {
            if (res.data?.verificationMode?.agentToHost?.agentId !== '0') {
              setActiveTab('1')
            } else {
              setActiveTab('2')
            }
          })
          .catch(() => {})
        getUseCaseList({ result_id: id, task_id: task_id, ...filterParams })
          .then(res => {
            setUseCaseList(res.data.items)
            setUseCaseTotal(res.data.total)
            setFixPayloadCount(res.data.manualCorrectCount)
          })
          .catch(() => {})
      })
      .catch(_ => {})
  }

  const tagTreeData = useMemo(() => {
    let listMappings: {
      title: string
      list: any[]
      total: number
      key: string
    }[] = []
    if (taskInfo.devices?.length > 0) {
      listMappings = [
        {
          title: t('用例分级'),
          list: taskDetailFilterData?.caseLevelList || [],
          total: taskDetailFilterData?.caseLevelTotal || 0,
          key: 'resourceLevel',
        },
        {
          title: t('危险等级'),
          list: taskDetailFilterData?.riskLevelList || [],
          total: taskDetailFilterData?.riskLevelTotal || 0,
          key: 'riskLevel',
        },
        {
          title: t('验证结果'),
          list: taskDetailFilterData?.resourceStatusList || [],
          total: taskDetailFilterData?.resourceStatusTotal || 0,
          key: 'status',
        },
        {
          title: t('安全设备'),
          list: taskDetailFilterData?.deviceList || [],
          total: taskDetailFilterData?.deviceTotal || 0,
          key: 'deviceIds',
        },
      ]
    } else {
      listMappings = [
        {
          title: t('用例分级'),
          list: taskDetailFilterData?.caseLevelList || [],
          total: taskDetailFilterData?.caseLevelTotal || 0,
          key: 'resourceLevel',
        },
        {
          title: t('危险等级'),
          list: taskDetailFilterData?.riskLevelList || [],
          total: taskDetailFilterData?.riskLevelTotal || 0,
          key: 'riskLevel',
        },
        {
          title: t('验证结果'),
          list: taskDetailFilterData?.resourceStatusList || [],
          total: taskDetailFilterData?.resourceStatusTotal || 0,
          key: 'status',
        },
      ]
    }
    return listMappings.map(category => ({
      title: category.title,
      key: category.key,
      extra: category.total,
      children: category.list.map(item => ({
        title: item.fieldName,
        key: `${category.key}_${item.fieldId}`,
        extra: item.resourceCount,
      })),
    }))
  }, [taskDetailFilterData, taskInfo])

  const getEchartsData = (result_id: string | undefined = id) => {
    getProtectRate({ resultId: result_id }).catch(_ => {})
    getRiskRate({ result_id }).catch(_ => {})
    getVerifyResultDistribution(result_id).catch(_ => {})
    getCaseLevelDistribution(result_id).catch(_ => {})
    getTemplateProtectionFailureRate(result_id).catch(_ => {})
    getDeviceProtectionRanking(result_id).catch(_ => {})
  }

  const refreshRequest = (result_id = id) => {
    getTaskInfo({ result_id, task_id })
      .then(res => {
        if (res.data?.verificationMode?.agentToHost?.agentId !== '0') {
          setActiveTab('1')
        } else {
          setActiveTab('2')
        }
      })
      .catch(_ => {})
    getAttackType({ resultId: result_id }).catch(_ => {})
    getTemplateNameList(result_id).catch(_ => {})
    getAttck({ result_id, task_id }).catch(_ => {})
    // getDevices({ result_id }).catch(_ => {})
    getTagTreeOptions({ taskResultId: result_id }).catch(_ => {})
    getTaskDetailFilterData(result_id).catch(_ => {})
    getOfflineLogInfo(result_id).catch(_ => {})
    getSentFailedCaseList(result_id).catch(_ => {})
    getUnsentCaseList(result_id).catch(_ => {})
    getCancelVerifyCaseList(result_id).catch(_ => {})

    setFilterParams({ page: 1, limit: 20 })
  }

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<any> | SorterResult<any>[],
  ) => {
    setFilterParams(prev => {
      // 保留之前的筛选条件，只更新发生变化的筛选条件
      const newFilters = { ...prev }

      // 处理表头筛选条件
      Object.keys(filters).forEach(key => {
        if (filters[key] === null) {
          delete newFilters[key]
        } else {
          if (key === 'deviceStatusList') {
            newFilters.deviceStatus = filters[key]
          } else {
            newFilters[key] = filters[key]
          }
        }
      })

      // 保留filterFormValue的筛选条件
      if (filterFormValue?.field && filterFormValue?.value) {
        const processedValue =
          filterFormValue.field === 'tag_ids'
            ? (filterFormValue.value as string[])?.map(item => item.split('$').slice(1).join('$'))
            : filterFormValue.value
        newFilters[filterFormValue.field] = processedValue
      }

      return {
        ...newFilters,
        page: pagination.current || 1,
        limit: pagination.pageSize || 20,
        sortOrder:
          (Array.isArray(sorter) ? sorter[0]?.order : sorter?.order) === 'descend'
            ? 'SORT_ORDER_DESCENDING'
            : (Array.isArray(sorter) ? sorter[0]?.order : sorter?.order) === 'ascend'
            ? 'SORT_ORDER_ASCENDING'
            : undefined,
      }
    })
  }

  useEffect(() => {
    if (filterFormValue?.field) {
      const selectedOption = option.find(opt => opt.value === filterFormValue.field)
      if (selectedOption) {
        setFilterParams(prev => {
          // 如果新值和旧值相同，则不更新
          if (prev[filterFormValue.field] === filterFormValue.value) {
            return prev
          }

          // 如果值为空字符串或空数组，说明是切换了字段，需要清除所有旧的筛选参数
          if (
            filterFormValue.value === '' ||
            (Array.isArray(filterFormValue.value) && filterFormValue.value.length === 0)
          ) {
            // 只保留分页相关参数
            return {
              page: 1,
              limit: prev.limit,
            }
          }

          const processedValue =
            filterFormValue.field === 'tagIds'
              ? (filterFormValue.value as string[])?.map(item => item.split('$').slice(1).join('$'))
              : filterFormValue.value

          // 如果值为空，则删除该筛选条件
          if (!processedValue) {
            const newFilters = { ...prev }
            delete newFilters[filterFormValue.field]
            return {
              ...newFilters,
              page: 1,
            }
          }

          // 否则更新或添加新的筛选条件
          return {
            ...prev,
            page: 1,
            [filterFormValue.field]: processedValue,
          }
        })
      }
    }
  }, [filterFormValue])

  useEffect(() => {
    getOfflineLogInfo(id).catch(_ => {})
    getTaskInfo({ result_id: id, task_id })
      .then(res => {
        if (res.data?.verificationMode?.agentToHost?.agentId !== '0') {
          setActiveTab('1')
        } else {
          setActiveTab('2')
        }
      })
      .catch(_ => {})
    getAttackType({ resultId: id }).catch(_ => {})
    getAttck({ result_id: id, task_id }).catch(_ => {})
    getTemplateNameList(id).catch(_ => {})
    // getDevices({ result_id: id }).catch(_ => {})
    getTagTreeOptions({ taskResultId: id }).catch(_ => {})
    getTaskDetailFilterData(id).catch(_ => {})
  }, [])

  useEffect(() => {
    getUseCaseList({ result_id: id, task_id, ...filterParams })
      .then(res => {
        setUseCaseList(res.data.items)
        setUseCaseTotal(res.data.total)
        setFixPayloadCount(res.data.manualCorrectCount)
      })
      .catch(_ => {})
  }, [filterParams])

  useEffect(() => {
    let intervalId
    if (importRecord.importing) {
      intervalId = setInterval(() => {
        getOfflineLogInfo(id)
          .then(res => {
            if (!res.data.importing) {
              refreshRequest()
              getEchartsData()
            }
          })
          .catch(_ => {})
      }, 3000)
    }

    return () => clearInterval(intervalId)
  }, [importRecord])

  const taskContent = (
    <TaskContent>
      {taskInfoModel.map((item, index) => {
        const value =
          item.type && item.type !== 'array' && item.type !== 'object'
            ? taskInfo[item.value as string]
            : item.value
        if (value && typeof value === 'object') {
          // 处理进度信息渲染
          return (
            <div key={index} className="progress-info">
              <span className="label">{item.key}：</span>
              {value.map((v, i) => {
                if (v.value === 'progressRate') {
                  return (
                    <Progress
                      key={i}
                      strokeColor={{
                        '0%': '#dfe7f9',
                        '100%': '#0346F2',
                      }}
                      success={{ percent: -1 }}
                      percent={taskInfo[v.value as string]}
                    />
                  )
                } else {
                  return (
                    <div
                      key={i}
                      className={v.value}
                      style={
                        v.value === 'isFinished'
                          ? {
                              background: statusMap[taskInfo?.[v.value as number]]?.background,
                              color: statusMap[taskInfo?.[v.value as number]]?.color,
                            }
                          : {}
                      }
                    >
                      {v.value === 'successSum' ? (
                        <Tooltip title={t('执行成功用例数量')}>
                          <span>{taskInfo?.[v.value as string]}</span>
                        </Tooltip>
                      ) : v.value === 'failSum' ? (
                        <Tooltip title={t('执行失败用例数量')}>
                          <span>{taskInfo?.[v.value as string]}</span>
                        </Tooltip>
                      ) : (
                        <span>
                          {v.value === 'isFinished'
                            ? statusMap[taskInfo?.[v.value as number]]?.label
                            : taskInfo?.[v.value as string]}
                        </span>
                      )}
                      {v.value === 'successSum' && (
                        <span style={{ color: '#666', margin: '0 4px' }}>/</span>
                      )}
                    </div>
                  )
                }
              })}
            </div>
          )
        } else {
          return (
            <div key={index}>
              <span className="label">{item.key}：</span>
              <span>{value || '-'}</span>
              <Divider type="vertical" />
            </div>
          )
        }
      })}
    </TaskContent>
  )

  const taskContentExpanded = (
    <TaskContentExpanded>
      {/* 任务信息 */}
      <div className="task-info">
        <div className="sub-title">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <SvgIcon width={16} height={16} name={'data-arrival'} fill={'#fff'} />
            <span className="text">{t('任务信息')}</span>
          </div>
        </div>
        <div className="content" style={{ alignItems: 'flex-start' }}>
          <div className="left">
            <div className="tabs">
              {taskInfo?.verificationMode?.agentToHost?.agentId !== '0' && (
                <div
                  className={`tab ${activeTab === '1' ? 'active' : ''}`}
                  onClick={() => setActiveTab('1')}
                >
                  <span>{t('验证模式1')}</span>
                </div>
              )}
              {taskInfo?.verificationMode?.agentToListener?.agentId !== '0' && (
                <div
                  className={`tab ${activeTab === '2' ? 'active' : ''}`}
                  onClick={() => setActiveTab('2')}
                >
                  <span>{t('验证模式2')}</span>
                </div>
              )}
            </div>
            <div className="tab-content">
              <div className="info">
                <div className="info-item">
                  <div className="label">{t('配置名称：')}</div>
                  <div className="value">
                    {activeTab === '1' ? t('Agent → 主机') : 'Agent → Listener'}
                  </div>
                </div>
                {activeTab === '1' ? (
                  <div className="info-group">
                    <div className="info-item">
                      <div className="label" style={{ paddingTop: 6 }}>
                        Agent：
                      </div>
                      <div className="value">
                        <Text
                          ellipsis={{ tooltip: { getPopupContainer: () => document.body } }}
                          style={{ width: 60, paddingTop: 6 }}
                        >
                          {taskInfo?.verificationMode?.agentToHost?.agentAlias || t('未命名')}
                        </Text>
                      </div>
                      <div className="label" style={{ paddingTop: 6 }}>
                        {t('主机名称：')}
                      </div>
                      <div className="value">
                        <Text
                          ellipsis={{ tooltip: { getPopupContainer: () => document.body } }}
                          style={{ width: 60, paddingTop: 6 }}
                        >
                          {taskInfo?.verificationMode?.agentToHost?.hostName || ''}
                        </Text>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="info-group">
                    <div className="info-item">
                      <div className="label" style={{ paddingTop: 6 }}>
                        Agent：
                      </div>
                      <div className="value">
                        <Text
                          ellipsis={{ tooltip: { getPopupContainer: () => document.body } }}
                          style={{ width: 60, paddingTop: 6 }}
                        >
                          {taskInfo?.verificationMode?.agentToListener?.agentAlias || t('未命名')}
                        </Text>
                      </div>
                      <div className="label" style={{ paddingTop: 6 }}>
                        {t('主机名称：')}
                      </div>
                      <div className="value">
                        <Text
                          ellipsis={{ tooltip: { getPopupContainer: () => document.body } }}
                          style={{ width: 60, paddingTop: 6 }}
                        >
                          {taskInfo?.verificationMode?.agentToListener?.hostName || ''}
                        </Text>
                      </div>
                    </div>
                  </div>
                )}
                <div className="info-item">
                  <div className="label">{t('安全域：')}</div>
                  <div className="value">
                    {activeTab === '1'
                      ? taskInfo?.verificationMode?.agentToHost?.areaName || t('默认安全域')
                      : taskInfo?.verificationMode?.agentToListener?.areaName || t('默认安全域')}
                  </div>
                </div>
              </div>
              <div className="item">
                <div className="label">{t('任务分组：')}</div>
                <div className="value">{taskInfo?.groupName || '-'}</div>
              </div>
              <div className="item">
                <div className="label">{t('执行类型：')}</div>
                <div className="value">
                  {taskInfo?.taskKind === 'TaskKindDetailImmediately'
                    ? t('立即执行')
                    : taskInfo?.taskKind === 'TaskKindDetailBooking'
                    ? t('预定执行')
                    : taskInfo?.taskKind === 'TaskKindDetailCycleDay'
                    ? t('每日执行')
                    : taskInfo?.taskKind === 'TaskKindDetailCycleWeek'
                    ? t('每周执行')
                    : taskInfo?.taskKind === 'TaskKindDetailCycleMonth'
                    ? t('每月执行')
                    : '-'}

                  {/* <Text
                   ellipsis={{ tooltip: { getPopupContainer: () => document.body } }}
                   style={{ width: 120, marginLeft: 10 }}
                  >
                   {taskInfo?.taskTimeList?.join(',') || '-'}
                  </Text> */}
                </div>
              </div>
              <div className="item">
                <div className="label">{t('执行速率：')}</div>
                <div className="value">
                  {taskInfo?.sampleDeliveryRate
                    ? `${taskInfo?.sampleDeliveryRate}${t('个/秒')}`
                    : '-'}
                </div>
              </div>
              <div className="item">
                <div className="label">{t('任务优先级：')}</div>
                <div className="value">{taskInfo?.priority || '-'}</div>
              </div>
              <div className="item">
                <div className="label">{t('创建时间：')}</div>
                <div className="value">{formatDateTime(taskInfo?.createTime, 'standard')}</div>
              </div>
            </div>
          </div>
          <div className="clearfix" />
          <div className="right">
            <div className="tabs">
              <div
                className={`tab ${activeRightTab === '1' ? 'active' : ''}`}
                onClick={() => setActiveRightTab('1')}
              >
                <span>{t('任务模板')}</span>
              </div>
              <div
                className={`tab ${activeRightTab === '2' ? 'active' : ''}`}
                onClick={() => setActiveRightTab('2')}
              >
                <span>{t('安全设备')}</span>
              </div>
            </div>
            <div className="tab-content">
              {activeRightTab === '1'
                ? taskInfo?.templates?.map((item, index) => (
                    <div className="item" key={index}>
                      <Text
                        ellipsis={{ tooltip: { getPopupContainer: () => document.body } }}
                        style={{ width: 350 }}
                      >
                        {item}
                      </Text>
                    </div>
                  ))
                : taskInfo?.devices?.map((item, index) => (
                    <div className="item" key={index}>
                      <span>{item.deviceType}</span>
                      <span>|</span>
                      <span>{item.deviceName}</span>
                      <span>|</span>
                      <span>{item.deviceIp}</span>
                    </div>
                  ))}
            </div>
          </div>
        </div>
      </div>
      {/* 进度信息 */}
      <div className="progress-info">
        {/* useCase 列表 */}
        <div style={{ display: !logInfo.visible ? 'block' : 'none' }}>
          <div className="sub-title">
            <div className="left">
              <SvgIcon
                width={16}
                height={16}
                name={'task-detail-schedule'}
                fill={'#fff'}
                style={{ marginTop: '5px' }}
              />

              <span className="text">{t('执行信息')}</span>
            </div>
            <div className="create-time">
              <span style={{ marginRight: 6 }}>
                {t('执行时间：')}
                {taskInfo?.startTime}
                <span style={{ margin: '0 6px' }}>{t('至')}</span>
                {taskInfo?.endTime}
              </span>
              <span style={{ marginLeft: 6 }}>
                {t('时长：共')}
                {formatTimeInterval(taskInfo?.startTime, taskInfo?.endTime)}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px' }}>
            <div
              className="status"
              style={{
                background: statusMap[taskInfo?.isFinished]?.background,
                color: statusMap[taskInfo?.isFinished]?.color,
              }}
            >
              <span>{statusMap[taskInfo?.isFinished]?.label || '-'}</span>
            </div>
            <Progress
              className="progress"
              strokeColor={{
                '0%': '#ECEBF9',
                '100%': '#5047CC',
              }}
              success={{ percent: -1 }}
              percent={taskInfo?.progressRate}
            />
          </div>
          <div className="info">
            <div className="tabs">
              <div
                className={`tab ${activeProgressTab === '1' ? 'active' : ''}`}
                onClick={() => setActiveProgressTab('1')}
              >
                <span>
                  {t('执行失败')} ({failSum || 0})
                </span>
              </div>
              <div
                className={`tab ${activeProgressTab === '2' ? 'active' : ''}`}
                onClick={() => setActiveProgressTab('2')}
              >
                <span>
                  {t('未执行')} ({unexecuteSum || 0})
                </span>
              </div>
              <div
                className={`tab ${activeProgressTab === '3' ? 'active' : ''}`}
                onClick={() => setActiveProgressTab('3')}
              >
                <span>
                  {t('取消验证')} ({unvalidSum || 0})
                </span>
              </div>
            </div>
            <div className="success-sum">
              <span>{t('执行成功：')}</span>
              <span style={{ color: '#67C23A' }}>{taskInfo?.successSum || 0}</span>
              <span>{t('个')}</span>
            </div>
          </div>
          <div className="list">
            <ProgressContent
              setLogInfo={setLogInfo}
              contentList={progressContentList}
              wrapperHeight={170}
              type={activeProgressTab === '2' ? 2 : 1}
            />
          </div>
        </div>
        {/* 执行日志 */}
        <div style={{ display: logInfo.visible ? 'block' : 'none' }}>
          <div className="sub-title">
            <div className="left">
              <Button
                className="return-btn"
                icon={<IconFont type="icon-fanhui2" className="return-icon" />}
                onClick={() =>
                  setLogInfo(origin => ({ ...origin, visible: false, content: '', id: '' }))
                }
              />

              <span className="text">{t('执行日志：')}</span>
            </div>
          </div>
          <div className="log-content">
            <div className="log-panel">
              <div className="content">
                {logInfo.content && <div className="log-content">{logInfo.content}</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </TaskContentExpanded>
  )

  return (
    <DetailContainer>
      <DetailTemplate
        scene={taskInfo?.devices?.length > 0 ? 'ES_device' : displayModel.name}
        taskName={taskInfo.taskName}
        taskStatus={taskInfo.isFinished}
        isImporting={importRecord.importing}
        canImport={importRecord.canImport}
        taskContent={taskContent}
        taskContentExpanded={taskContentExpanded}
        taskCharts={
          <TaskCharts
            showDeviceChart={taskInfo?.devices?.length > 0}
            taskStatus={taskInfo.isFinished}
            taskScore={taskInfo?.score === '-' ? '-' : Number(taskInfo?.score)}
            failRate={verifyResultDistribution}
            protectedRate={protectedRate}
            riskRate={riskRate}
            resourceRate={caseLevelDistribution}
            templateRate={templateProtectionFailureRate}
            deviceRate={deviceProtectionRanking}
          />
        }
        treeData={tagTreeData}
        handleExpand={handleExpand}
        filterOptions={option}
        filterFormValue={filterFormValue}
        setFilterFormValue={setFilterFormValue}
        filterParams={filterParams}
        setFilterParams={setFilterParams}
        columns={columns}
        dataSource={useCaseList}
        total={useCaseTotal}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        rowSelection={rowSelection}
        fixedCount={fixPayloadCount}
        fixedOptions={fixedOptions}
        handleFixed={handleFixed}
        fixedLoading={updateInterceptStatusLoading}
        getEchartsData={getEchartsData}
        refreshRequest={refreshRequest}
        loading={loading}
        handleBack={handleBack}
        openRecordModal={setRecordVisible}
        openImportModal={setImportVisible}
        openReportModal={setReportVisible}
        openDeleteModal={setDeleteVisible}
        fixedModalVisible={fixedModalVisible}
        setFixedModalVisible={setFixedModalVisible}
        onTableChange={onTableChange}
        scroll={{ x: 500 }}
      />

      <RecordModal
        open={recordVisible}
        loading={deleteRecordLoading}
        dataSource={importRecord.logDeviceRecords}
        onClose={() => setRecordVisible(false)}
        handleDelete={deleteRecord}
      />

      <ImportModal
        open={importVisible}
        type={'ES'}
        resultId={id}
        refreshPage={() => {
          refreshRequest()
          getEchartsData()
        }}
        onClose={() => setImportVisible(false)}
        options={[
          { label: 'EDR', value: 'EDR', disabled: false },
          { label: 'HIDS', value: 'HIDS', disabled: false },
          { label: 'AV', value: 'AV', disabled: false },
          { label: 'SOC', value: 'SOC', disabled: false },
        ]}
        deviceTypeValue={'EDR'}
      />

      <CreateReport
        visible={reportVisible.visible}
        createLoading={createLoading}
        templateType={9}
        onConfirm={e => {
          createReport(e)
        }}
        onCancel={() => {
          setReportVisible(origin => ({ ...origin, visible: false }))
        }}
      />

      <DeleteModal
        onOk={handleDelete}
        visible={deleteVisible}
        title={t('删除任务')}
        text={t('此操作将永久删除该任务，是否继续？')}
        onCancel={() => {
          setDeleteVisible(false)
        }}
      />

      <DetailDrawer
        onClose={() => {
          setDetailDrawerVisible(false)
        }}
        dataList={pluginDetail}
        logList={pluginDetail?.device_security}
        visible={detailDrawerVisible}
        loading={detailLoading}
      />
    </DetailContainer>
  )
}

export default Detail

const DetailContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  padding-bottom: 16px;
  .case_name {
    &:hover {
      text-decoration: underline;
    }
  }
`

const TaskContent = styled.div`
  display: flex;
  align-items: center;

  .label {
    color: #86909c;
    margin-right: 4px;
    flex-shrink: 0;
  }

  .ant-divider-vertical {
    margin: 0 19px;
  }

  .progress-info {
    flex: 1;
    display: flex;
    height: 100%;
    width: 50%;

    .isFinished {
      width: 50px;
      height: 18px;
      border-radius: 9px;
      font-size: 12px;
      text-align: center;
      line-height: 18px;
      flex-shrink: 0;
      margin-right: 10px;
    }

    .ant-progress-line {
      font-size: 12px;
      width: 90%;
      margin-right: 20px;
    }

    .successSum {
      color: #3586ff;
      font-size: 12px;
      flex-shrink: 0;
    }

    .failSum {
      color: #f5222d;
      font-size: 12px;
      flex-shrink: 0;
    }
  }
`

const TaskContentExpanded = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .task-info {
    flex: 1;
    height: 100%;
    min-height: 320px;
    margin-right: 16px;
    border-radius: 8px;
    border: 1px solid #e1e3e4;
    width: 50%;

    .sub-title {
      height: 40px;
      display: flex;
      align-items: center;
      border-bottom: 1px solid #e1e3e4;
      padding-left: 18px;
      justify-content: space-between;

      .text {
        margin-left: 10px;
        font-weight: 500;
        font-size: 16px;
      }
    }

    .content {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .left {
        flex: 1;
        height: 100%;
        font-size: 14px;
        border-right: 1px solid #e1e3e4;

        .tabs {
          height: 32px;
          background: #eeeff1;
          display: flex;
          align-items: center;

          .tab {
            height: 32px;
            width: 84px;
            height: 100%;
            text-align: center;
            line-height: 32px;

            &.active {
              background: #e3e1f5;
              color: #4238c9;
            }

            &:hover {
              background: #e3e1f5;
              color: #4238c9;
              cursor: pointer;
            }
          }
        }

        .tab-content {
          padding: 14px 12px 0 12px;

          .info {
            background: #f8f9fb;
            border-radius: 6px;
            padding: 8px;
            margin-bottom: 8px;

            .info-item {
              display: flex;
              align-items: center;
              margin-bottom: 8px;

              &:first-child {
                margin-bottom: 0;
              }

              &:last-child {
                margin-top: 8px;
              }

              .label {
                width: 70px;
                flex-shrink: 0;
                color: #86909c;
              }

              &:last-child {
                margin-bottom: 0;
              }
            }

            .info-group {
              display: flex;
              align-items: center;
              justify-content: space-between;
            }
          }

          .item {
            display: flex;
            align-items: center;
            margin-bottom: 12px;

            .label {
              color: #86909c;
            }

            .value {
              color: #333;
            }

            &:last-child {
              margin-bottom: 4px;
            }
          }
        }

        .left-item {
          margin-bottom: 18px;
          display: flex;
          align-items: center;

          .label {
            width: 70px;
            flex-shrink: 0;
            color: #86909c;
          }

          .type-text {
            height: 32px;
            background: #fff;
            border: 1px solid #d9d9d9;
            border-right: 0;
            border-radius: 6px 0 0 6px;
            padding: 8px;
          }

          .type-select {
            width: 180px;

            .ant-select-selector {
              border-radius: 0 6px 6px 0;
            }

            .ant-select-selection-placeholder {
              color: #333;
            }
          }
        }
      }

      .right {
        flex: 1;
        height: 243px;

        .tabs {
          height: 32px;
          background: #eeeff1;
          display: flex;
          align-items: center;

          .tab {
            height: 32px;
            width: 84px;
            height: 100%;
            text-align: center;
            line-height: 32px;

            &.active {
              background: #e3e1f5;
              color: #4238c9;
            }

            &:hover {
              background: #e3e1f5;
              color: #4238c9;
              cursor: pointer;
            }
          }
        }

        .tab-content {
          padding: 12px 0 0 12px;
          overflow-y: scroll;
          height: 100%;

          .item {
            color: #666666;
            margin-bottom: 12px;
          }
        }

        .ant-tabs {
          height: 222px;
          border-radius: 6px;

          .ant-tabs-nav {
            padding: 0 12px;
            background: #f1f3f5;
            margin-bottom: 8px;

            .ant-tabs-tab {
              color: #86909c;
            }

            .ant-tabs-tab.ant-tabs-tab-active {
              color: #4238c9;
            }
          }

          .tab-content {
            padding: 0 12px;
            overflow-y: scroll;
            max-height: 170px;
            max-width: 256px;

            .template {
              width: 100%;
              font-size: 12px;
              line-height: 18px;
            }
          }
        }
      }
    }
  }

  .progress-info {
    flex: 1;
    height: 100%;
    border-radius: 8px;
    min-height: 312px;
    padding-bottom: 5px;
    border: 1px solid #e1e3e4;
    width: 50%;

    .sub-title {
      height: 35px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid #e1e3e4;
      padding-left: 18px;
      padding-right: 12px;

      .left {
        display: flex;

        .return-btn {
          width: 24px;
          height: 24px;
          display: flex;
          justify-content: center;
          justify-items: center;
          align-items: center;
          margin-right: 12px;
          background: #fff;
          border: 0px;

          &:hover {
            border: 1px solid #4238c9;
            color: #4238c9;
          }
        }

        .return-icon {
          font-size: 12px;
        }

        .text {
          margin-left: 10px;
          font-weight: 500;
          font-size: 16px;
          margin-right: 6px;
          line-height: 24px;
        }

        .status {
          width: 50px;
          height: 18px;
          border-radius: 9px;
          font-size: 12px;
          text-align: center;
          line-height: 18px;
          flex-shrink: 0;
          margin-right: 10px;
        }
      }

      .create-time {
        color: '#666';
      }
    }

    .log-content {
      height: 260px;

      .log-panel {
        border-radius: 8px;
        height: 235px;
        display: flex;
        flex-direction: column;

        .header {
          height: 36px;
          border-radius: 8px 8px 0 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0 16px;

          .left {
            height: 100%;
            display: flex;
            padding-top: 10px;

            span:first-child {
              width: 14px;
              height: 14px;
              background: #ff5f56;
              border-radius: 50%;
            }

            span:nth-child(2) {
              width: 14px;
              height: 14px;
              background: #ffbb2f;
              border-radius: 50%;
              margin: 0 8px;
            }

            span:last-child {
              width: 14px;
              height: 14px;
              background: #27c841;
              border-radius: 50%;
            }
          }

          .copy-btn .ant-typography .copy {
            color: #d0d0d0;

            &:hover {
              color: #fff;
            }
          }
        }

        .content {
          flex: 1;
          border-radius: 0 0 8px 8px;
          padding: 14px 12px;
          overflow-y: scroll;
          display: flex;
          flex-direction: column;

          span {
            color: #fff;
            line-height: 20px;
          }
        }
      }
    }

    .info {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .success-sum {
        margin-right: 12px;
      }
    }

    .status {
      width: 50px;
      height: 18px;
      border-radius: 9px;
      font-size: 12px;
      text-align: center;
      line-height: 18px;
      flex-shrink: 0;
      margin-right: 10px;
    }

    .progress {
      margin: 12px 0;
    }

    .tabs {
      height: 32px;
      background: #eeeff1;
      display: flex;
      align-items: center;

      .tab {
        height: 32px;
        width: 125px;
        height: 100%;
        text-align: center;
        line-height: 32px;

        &.active {
          background: #e3e1f5;
          color: #4238c9;
        }

        &:hover {
          background: #e3e1f5;
          color: #4238c9;
          cursor: pointer;
        }
      }
    }

    .list {
      min-height: 206px;
      padding: 12px;

      .item {
        height: 32px;
        background: #fafafa;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 12px;
        margin-bottom: 6px;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    .information {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 5px;
      color: #666;
      font-size: 12px;

      .success-sum {
        color: #73a0fa;
      }

      .failed-sum {
        color: #f44f4f;

        &:hover {
          cursor: pointer;
          text-decoration: underline;
        }
      }
    }
  }
`

const FilterIcon = styled(IconFont)<{ $filtered?: boolean }>`
  font-size: 14px;
  color: ${props => (props.$filtered ? '#4238C9' : '#999')};
  cursor: pointer;
  transition: color 0.3s;
  &:hover {
    color: #4238c9;
  }
`

const TypeTag = styled.span<{ bgColor: string; color: string }>`
  display: inline-block;
  height: 18px;
  padding: 0 4px;
  background: ${props => props.bgColor};
  border-radius: 4px;
  line-height: 18px;
  color: ${props => props.color};
  font-size: 12px;
  margin-right: 4px;
`
